import { useEffect, useState } from 'react';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper/index';
import CustomiseOffer from './CustomiseOffer';
import EarlyRepaymentCalculator from '../earlyRepaymentCalculator/EarlyRepayment';
import styles from './FlexiApprovedView.module.css';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { useQuery } from '@tanstack/react-query';
import {
    fetchGetFormalOffers,
    GetFormalOffersResponse
} from '@iwoca/lapi-client/edge';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import ApprovalHeader from '../approvalHeader/ApprovalHeader';
import { getAvailableCash, getTimeOutDate } from '../../MyOffers.helpers';
import { getMonthsfromDays } from '../../../multiLoanDashboard/accountsSelector';
import { getMaxDate } from '../earlyRepaymentCalculator/EarlyRepaymentHelper';
import { TitleSectionWrapper } from '../../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { tracking } from '@iwoca/frontend-tracking-library';
import { NewApprovedViewUI } from '../NewApprovedView/NewApprovedView';
import { useSplitTests } from '@api/lendingAPI/lapiHooks';
import { TrustPilot } from '../../../TrustPilot/TrustPilot';
import { FooterSectionWrapper } from '../../../pageStructure/footerSectionWrapper/FooterSectionwraper';
import { getCountry } from '@iwIntlProvider/CountryProvider';

export const FlexiApprovedView = () => {
    const [drawdownAmount, setDrawdownAmount] = useState<number>(0);
    const [selectedOffer, setSelectedOffer] = useState<
        NonNullable<GetFormalOffersResponse['data']>['formal_offers'][0] | null
    >(null);
    const stateKey = useStateKey();

    const showNewUI = useSplitTests('new_approval_ui_test_2024');

    const {
        isLoading,
        isError,
        data: getFormalOffersResponse
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey: stateKey! })
    });

    useEffect(() => {
        if (getFormalOffersResponse?.data) {
            setDrawdownAmount(
                getFormalOffersResponse!.data!.formal_offers[0].available_cash!
            );
        }
    }, [getFormalOffersResponse, setDrawdownAmount]);

    const updateSelectedOffer = (
        offer: NonNullable<GetFormalOffersResponse['data']>['formal_offers'][0]
    ) => {
        tracking.ampli.formalOfferSelected({
            formal_offer_id: offer.offer_id!
        });
        setSelectedOffer(offer);
    };

    if (!isError && (isLoading || drawdownAmount === 0)) {
        return <MainContentPending data-testid="flexi-approval-view-loading" />;
    }

    if (isError) {
        return <FallbackErrorModal />;
    }

    const maxCashAmount = getAvailableCash(
        getFormalOffersResponse!.data!.formal_offers
    );
    const timeoutDate = getTimeOutDate(
        getFormalOffersResponse!.data!.formal_offers
    );

    let maxDate = new Date().toISOString();
    let loanDuration = 0;

    if (selectedOffer) {
        loanDuration =
            selectedOffer.duration!.unit === 'days'
                ? getMonthsfromDays(selectedOffer.duration!.amount)
                : selectedOffer.duration!.amount;

        maxDate = getMaxDate(selectedOffer.duration?.amount);
    }

    if (showNewUI) {
        return (
            <NewApprovedViewUI
                maxDate={maxDate}
                loanDuration={loanDuration}
                drawdownAmount={drawdownAmount}
                setDrawdownAmount={setDrawdownAmount}
                selectedOffer={selectedOffer}
                updateSelectedOffer={updateSelectedOffer}
                formalOffers={getFormalOffersResponse.data!}
                timeoutDate={timeoutDate}
                maxCashAmount={maxCashAmount}
                showNewUI={showNewUI}
            />
        );
    } else
        return (
            <>
                <TitleSectionWrapper>
                    <ApprovalHeader
                        approvalStatusTimeOut={timeoutDate}
                        maxCashAmount={maxCashAmount}
                    />
                </TitleSectionWrapper>

                <MainSectionWrapper className={styles.mainSection}>
                    <div className={styles.approvalSection}>
                        <CustomiseOffer
                            drawdownAmount={drawdownAmount}
                            setDrawdownAmount={setDrawdownAmount}
                            selectedOffer={selectedOffer}
                            updateSelectedOffer={updateSelectedOffer}
                            formalOffers={getFormalOffersResponse.data!}
                            showNewUI={showNewUI}
                        />
                    </div>
                    {selectedOffer && (
                        <EarlyRepaymentCalculator
                            drawdownAmount={drawdownAmount}
                            offerId={selectedOffer.offer_id!}
                            loanDuration={loanDuration}
                            productType={selectedOffer.product_type!}
                            maxDate={maxDate}
                        />
                    )}
                </MainSectionWrapper>
                <FooterSectionWrapper>
                    {getCountry() === 'UK' && <TrustPilot />}
                </FooterSectionWrapper>
            </>
        );
};
